'use client';

import { useState } from 'react';

import Modal from '@/app/_components/modal';
import SelectCheckBox from '@/app/_components/widgets/listing/listingFilters/selectCheckbox';
import SelectRadioButton from '@/app/_components/widgets/listing/listingFilters/selectRadioButton';
import { dateOptions, locationOptions, paymentOptions } from '@/app/_components/widgets/listing/listingFilters/utils';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { FilterStates, useListingFilters } from '@/app/_hooks/useListingFilters';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Keep in sync with the `transition-transform duration-300` below so the modal unmounts only after the slide-down finishes.
const CLOSE_ANIMATION_MS = 300;

type FiltersFormModalProps = {
  onClose: () => void;
  onFiltersChange?: () => void;
};

export const FiltersFormModal = ({ onClose, onFiltersChange }: FiltersFormModalProps) => {
  const { filters, applyFilters, clearFilters } = useListingFilters();
  const [modalFilters, setModalFilters] = useState(filters);
  const [isClosing, setIsClosing] = useState(false);

  const isWelfare = useIsWelfare();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  const setModalFilter =
    <T extends keyof FilterStates>(filter: T) =>
    (value?: FilterStates[T]) => {
      const newFilters = { ...modalFilters, [filter]: value };
      setModalFilters(newFilters);
    };

  return (
    <Modal
      className={clsx(
        'mb-0 mt-auto flex max-h-[90%] w-[100vw] max-w-[100vw] flex-col items-center justify-between gap-4 rounded-t-xl px-3 py-5 transition-transform duration-300',
        'backdrop:animate-fadeIn backdrop:transition-opacity backdrop:duration-300',
        isClosing ? 'translate-y-full backdrop:opacity-0' : 'open:animate-slideBottom',
      )}
      onClose={handleClose}
      isModalOpen
    >
      <div className="flex w-full flex-col items-center justify-start">
        <div className="flex w-full items-center justify-between p-2">
          <div />
          <h1 className="text-base font-semibold text-primary">Filtri</h1>
          <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={handleClose} />
        </div>
        <div className="flex max-h-full w-full flex-col items-center justify-start overflow-y-auto">
          <SelectRadioButton
            label={dateOptions.title}
            options={[...dateOptions.options, { label: dateOptions.defaultOptionLabel, value: undefined }]}
            selected={modalFilters.dateFilter}
            setSelected={setModalFilter('dateFilter')}
          />
          {!isWelfare && (
            <SelectRadioButton
              label={paymentOptions.title}
              options={[...paymentOptions.options, { label: paymentOptions.defaultOptionLabel, value: undefined }]}
              selected={modalFilters.paymentFilter}
              setSelected={setModalFilter('paymentFilter')}
            />
          )}
          <SelectCheckBox
            label={locationOptions.title}
            options={[...locationOptions.options, { label: locationOptions.defaultOptionLabel, value: undefined }]}
            selected={modalFilters.channelFilter}
            setSelected={setModalFilter('channelFilter')}
          />
        </div>
      </div>
      <div className="flex w-full flex-col items-center justify-end gap-5 py-5">
        <ELButton
          size="large"
          fullWidth
          variant="filled"
          color="primary"
          label="Applica tutti i filtri"
          onClick={() => {
            onFiltersChange?.();
            applyFilters(modalFilters);
            handleClose();
          }}
          fullRadius
        />
        <ELButton
          size="large"
          fullWidth
          variant="text"
          color="primary"
          label="Annulla tutti i filtri"
          onClick={() => {
            onFiltersChange?.();
            clearFilters();
            handleClose();
          }}
          fullRadius
        />
      </div>
    </Modal>
  );
};
